/**
 *
 * TextArea
 *
 */
import React, { memo, ReactNode } from 'react';
import classNames from 'classnames';
import Modal from 'react-modal';
import StyleComponentProps from 'core/utils/types/StyleComponentProps';
import { ReactComponent as Close } from 'assets/icons/Close.svg';
import Swal from 'sweetalert2';
import DOMPurify from 'dompurify';
import httpclient from 'libs/Axios';
import { SetupLoader } from '../SetupComponents/SetupLoader';

interface Props extends StyleComponentProps {
  placeholder: string;
  label?: string;
  inputWidth?: string;
  type?: string;
  required?: boolean;
  value: string | number | undefined;
  onChange: React.ChangeEventHandler<HTMLTextAreaElement> | undefined;
  onClick?: React.MouseEventHandler<HTMLTextAreaElement> | undefined;
  onPaste?: React.ClipboardEventHandler<HTMLTextAreaElement> | undefined;
  className?: string;
  rightIcon?: ReactNode;
  rows?: number;
  onBlur?: any;
  error?: any;
  message?: any;
  name?: string;
  isDisabled?: boolean;
  maxLength?: number;
  resize?: boolean;
  editor?: boolean
}

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '100rem',
    minHeight: '40rem',
    maxHeight: '40rem',
    borderRadius: '1.5em',
    boxShadow: '0px 16px 32px 4px rgba(0, 0, 0, 0.15)',
    padding: '1.3em',
  },
  overlay: {
    background: 'rgba(0, 0, 0, 0.4)',
    zIndex: 999,
  },
};

// Editor Component
const TextEditor = ({ isOpen, onClose, value, onSave }: any) => {
  const editorRef: any = React.useRef(null);
  const [htmlContent, setHtmlContent] = React.useState(value || ""); // State to store HTML content for preview
  const [currentAlignment, setCurrentAlignment] = React.useState("left"); // Track alignment state
  const [loader, setLoader] = React.useState(false); // loader

  // Helper function to validate selection
  const isSelectionValid = () => {
    const selection = window.getSelection();
    return selection && selection.rangeCount > 0 && !selection.isCollapsed;
  };

  const applyBold = () => {
    document.execCommand("bold", false);
  };

  const applyItalic = () => {
    document.execCommand("italic", false);
  };

  const handleFileUpload = async (file, type) => {
    try {
      setLoader(() => true);
      // Create a FormData object to send the file
      const formData = new FormData();
      formData.append("files", file);
      const response: any = await httpclient().post(
        `/data/upload/tempfiles`,
        formData, {
        headers: {
          'Custom-Header': 'multipart/form-data', // Add custom headers for this specific request
        },
      }
      );
      // Reference to the editor
      const editor = document.getElementById("editoraskjdhasdbaksjdkabsdasd");

      // Create a placeholder for typing above the media
      const placeholderAbove = document.createElement("div");
      // placeholderAbove.contentEditable = "true";
      placeholderAbove.innerHTML = "<br>"; // Empty space for typing
      placeholderAbove.className = "w-full my-2";

      // Create a placeholder for typing below the media
      const placeholderBelow = document.createElement("div");
      // placeholderBelow.contentEditable = "true";
      placeholderBelow.innerHTML = "<br>"; // Empty space for typing
      placeholderBelow.className = "w-full my-2";

      // Create the media element (image or video)
      let mediaElement;
      if (type === "image") {
        mediaElement = document.createElement("img");
        mediaElement.src = response?.data?.data?.[0];
        mediaElement.alt = "Image";
        // mediaElement.className = "my-4";
      } else if (type === "video") {
        mediaElement = document.createElement("video");
        mediaElement.controls = true;
        mediaElement.className = "w-full";

        const sourceElement = document.createElement("source");
        sourceElement.src = response?.data?.data?.[0];
        sourceElement.type = "video/mp4";

        mediaElement.appendChild(sourceElement);
      }

      // Append the placeholders and media to the editor
      if (editor) {
        editor.appendChild(placeholderAbove); // Above the media
        editor.appendChild(mediaElement); // Media itself
        editor.appendChild(placeholderBelow); // Below the media
      }

      // Focus on the placeholder below the media
      placeholderBelow.focus();
      setLoader(() => false);
    } catch (error) {
      setLoader(() => false);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "File upload failed. Please try again.",
      });
    }
  };

  // Insert Image
  const insertImage = () => {
    Swal.fire({
      title: "Select a Image",
      html: '<div class="w-full"><input type="file" id="file-input" accept="image/*" /></div>',
      showCancelButton: true,
      confirmButtonText: "Upload",
      customClass: {
        confirmButton: "bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600", // Confirmation button styles
        cancelButton: "bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400", // Cancel button styles
      },
      preConfirm: () => {
        const fileInput: any = document.getElementById("file-input");
        const file = fileInput?.files[0];
        if (!file) {
          Swal.showValidationMessage("Please select a file.");
        }
        return file;
      },
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        handleFileUpload(result.value, "image");
      }
    });
  };

  // Insert Video
  const insertVideo = () => {
    Swal.fire({
      title: "Select a Video",
      html: '<div class="w-full"><input type="file" id="file-input" accept="video/mp4,video/x-m4v,video/*" /></div>',
      showCancelButton: true,
      confirmButtonText: "Upload",
      customClass: {
        confirmButton: "bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600", // Confirmation button styles
        cancelButton: "bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400", // Cancel button styles
      },
      preConfirm: () => {
        const fileInput: any = document.getElementById("file-input");
        const file = fileInput?.files[0];
        if (!file) {
          Swal.showValidationMessage("Please select a file.");
        }
        return file;
      },
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        handleFileUpload(result.value, "video");
      }
    });
  };

  // Resize Text
  const resizeText = (fontSize: string) => {
    if (isSelectionValid()) {
      document.execCommand("fontSize", false, "7"); // Temporary size
      const editor = editorRef.current;
      const spanElements = editor.querySelectorAll("font[size='7']");
      spanElements.forEach((span: any) => {
        span.removeAttribute("size");
        span.style.fontSize = fontSize;
      });
    }
  };

  // Insert Link
  const insertLink = () => {
    const linkUrl = prompt("Enter the URL for the link:");
    if (linkUrl) {
      const selection: any = window.getSelection();
      if (selection?.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const link = document.createElement("a");
        link.href = linkUrl;
        link.target = "_blank"; // Open link in a new tab
        link.textContent = selection.toString(); // Use the selected text for the link text

        // Insert the link at the cursor position
        range.deleteContents();
        range.insertNode(link);
      }
    }
  };

  const convertToNormalText = () => {
    if (isSelectionValid()) {
      document.execCommand("removeFormat", false);
    }
  };

  // Apply Alignment
  const applyAlignment = (alignment: string) => {
    setCurrentAlignment(alignment); // Update alignment state
    document.execCommand(alignment, false);
  };

  // Reset alignment when a new line is created
  const handleKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      setTimeout(() => {
        document.execCommand(currentAlignment, false);
      }, 0); // Delay to ensure alignment is applied after the new line is created
    }
  };

  // Function to handle preview
  const handlePreview = () => {
    if (editorRef.current) {
      setHtmlContent(editorRef.current.innerHTML); // Store the current HTML content of the editor
    }
  };

  const handleSave = () => {
    if (editorRef.current) {
      onSave(editorRef.current.innerHTML); // Save updated content
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      contentLabel="Example Modal"
      style={customStyles}
    >
      <div className="w-full">
        <div className="flex justify-between pb-4">
          <h1 className="font-bold text-xl">
            Editor
          </h1>
          <Close
            className="w-6 h-6 cursor-pointer"
            onClick={() => onClose()}
          />
        </div>
        {loader ? <SetupLoader height='h-10  mb-4' /> : <div className="flex justify-between items-center flex-wrap gap-4 mb-4">
          <button
            onClick={insertImage}
            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
          >
            Insert Image
          </button>
          <button
            onClick={insertVideo}
            className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600"
          >
            Insert Video
          </button>
          <button
            onClick={convertToNormalText}
            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
          >
            Normal
          </button>
          <button
            onClick={applyBold}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Bold
          </button>
          <button
            onClick={applyItalic}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
          >
            Italic
          </button>
          <button
            onClick={insertLink}
            className="bg-blue-700 text-white px-4 py-2 rounded hover:bg-blue-800"
          >
            Insert Link
          </button>
          <button
            onClick={() => applyAlignment("justifyLeft")}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Text Left
          </button>
          <button
            onClick={() => applyAlignment("justifyCenter")}
            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
          >
            Text Center
          </button>
          <button
            onClick={() => applyAlignment("justifyRight")}
            className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600"
          >
            Text Right
          </button>
          <select
            onChange={(e) => resizeText(e.target.value)}
            defaultValue=""
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded"
          >
            <option value="" disabled>
              Resize Text
            </option>
            <option value="12px">Small</option>
            <option value="16px">Medium</option>
            <option value="20px">Large</option>
            <option value="24px">Extra Large</option>
          </select>
          <button
            onClick={handlePreview}
            className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700"
          >
            Preview
          </button>
          <button
            onClick={handleSave}
            className="bg-orange-700 text-white px-4 py-2 rounded hover:bg-teal-700"
          >
            Done
          </button>
          {/* <button
            onClick={() => applyAlignment("justifyFull")}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
          >
            Justify
          </button> */}
        </div>}
        <div className="flex gap-8">
          <div
            id={"editoraskjdhasdbaksjdkabsdasd"}
            ref={editorRef}
            contentEditable
            className="border border-gray-300 rounded p-4 min-h-[55vh] focus:outline-none flex-1"
            spellCheck="false"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(htmlContent) }} // Set default or initial content
            onKeyUp={handleKeyUp}
          ></div>
          <div
            className="flex-1 p-4 border border-gray-300 rounded min-h-[55vh] overflow-auto"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(htmlContent) }}
          ></div>
        </div>
      </div>
    </Modal>
  );
};


export const TextArea = memo((props: Props) => {

  const [isEditorOpen, setIsEditorOpen] = React.useState(false);

  const handleEditorSave = (updatedContent: string) => {
    if (props?.onChange) {
      const fakeEvent = { target: { value: updatedContent } }; // Mimic onChange event
      props?.onChange(fakeEvent as React.ChangeEvent<HTMLTextAreaElement>);
    }
  };

  return (
    <div
      className={`${props.width ? props.width : 'w-full'} ${props.margin ? props.margin : 'm-auto'} my-2 ${props.className}`}
    >
      {!!isEditorOpen && <TextEditor
        isOpen={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        value={props?.value}
        onSave={handleEditorSave}
      />}
      <div className="flex gap-4 justify-between items-center mb-[4px]">
        {!!props.label && (
          <label className={`${props.className} inline-block text-sm`}>
            {props.label}{' '}
            {props.required && <span className="text-secondary-red">*</span>}
          </label>)}
        {!!props?.editor && <button className="bg-primary-main text-neutral-light px-3 py-1 rounded-lg" type="button" onClick={() => setIsEditorOpen(true)}>Editor</button>}
      </div>
      <textarea
        disabled={props.isDisabled}
        onBlur={props.onBlur}
        name={props.name}
        value={props.value}
        onChange={props.onChange}
        onClick={props.onClick}
        onPaste={props.onPaste}
        maxLength={props?.maxLength}
        placeholder={props.placeholder}
        className={classNames(
          `bg-neutral-light w-full border-[1px] min-h-max ${props?.resize === false && `resize-none`} ${props.height || 'h-[44px]'
          } ${props.error && 'border-secondary-red'} ${props.height ? props.height : 'h-[44px]'
          } ${props.inputWidth ? props.inputWidth : 'h-[44px]'
          } rounded-[6px] p-[12px] focus:outline-4 focus:outline-primary-40 focus:border-[1px] focus:border-primary-main ${props.className
          }`,
          {},
        )}
      ></textarea>
      {props?.rightIcon}

      {props.message && (
        <p
          className={classNames(
            `${props.error ? 'text-secondary-red' : 'text-neutral-50'}
        mt-[4px] text-xs
        `,
          )}
        >
          {props.message}
        </p>
      )}
    </div>
  );
});
